import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ApiConfig } from '@vantage-hub/brand-kit'
import type { AxiosInstance } from 'axios'
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { createApiClient } from './create-client.js'

interface ApiClientContextValue {
  client: AxiosInstance
}

const ApiClientContext = createContext<ApiClientContextValue | null>(null)

interface ApiClientProviderProps {
  config: ApiConfig
  children: ReactNode
  /** Optional pre-configured QueryClient — provide in tests */
  queryClient?: QueryClient
}

const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: (failureCount, error) => {
        // Do not retry on 4xx errors
        if (
          error instanceof Error &&
          'status' in error &&
          typeof (error as { status?: number }).status === 'number' &&
          (error as { status: number }).status < 500
        ) {
          return false
        }
        return failureCount < 3
      },
    },
  },
})

export function ApiClientProvider({
  config,
  children,
  queryClient = defaultQueryClient,
}: ApiClientProviderProps) {
  const client = useMemo(() => createApiClient(config), [config])

  return (
    <ApiClientContext.Provider value={{ client }}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ApiClientContext.Provider>
  )
}

export function useApiClient(): AxiosInstance {
  const context = useContext(ApiClientContext)
  if (!context) {
    throw new Error('useApiClient must be used inside <ApiClientProvider>')
  }
  return context.client
}
